import { PLAYGAMA_CLIDS } from '@/constants/general'
import type { User } from '@/types'
import type { ApiRequests } from '@/utils/requests/api-requests'

import type { DisableAdOrder, Order, PurchaseStatus, Topup } from '.'

export type PurchasePaymentOrder = Order | DisableAdOrder
export type PaymentOrder = PurchasePaymentOrder | Topup
export type ConfirmedFunding = { amount: number; transactionId?: string }

const CURRENCY_PURCHASE_URL = '/api/v1/in-game-payments/purchase/from-currency/signed/gam'

/** The wallet-side cost of a purchase, whatever currency it is charged in. */
export function getPurchaseGamAmount(order: PurchasePaymentOrder) {
    return order.type === 'order' ? order.gamAmount : order.amount
}

type ProcessPurchaseParams = {
    api: ApiRequests
    clid?: string
    order: PurchasePaymentOrder
    transactionId?: string
}

export async function processInGamePaymentPurchase({ api, clid, order, transactionId }: ProcessPurchaseParams) {
    const { placement, ...orderObj } = order
    const url = orderObj.type === 'disable-ad-order' ? '/api/v1/user/disable-ad' : undefined
    const result = await processPurchaseOrder(api, orderObj, { clid, transactionId, url })

    if (result.errors.length > 0) {
        throw new Error(result.errors.join(', '))
    }

    if (result.data?.status !== 'PAID') {
        throw new Error(result.data?.status === 'FAILED' ? result.data.error : 'Purchase did not return a paid status')
    }

    return result
}

type UserRefresh = { user: User | null; error: unknown }

type FulfillmentParams<T extends PaymentOrder> = {
    api: ApiRequests
    clid?: string
    order: T
    payment?: ConfirmedFunding
    refreshUser: () => Promise<User | null>
}

type TopupFulfillment = { status: 'topup-complete'; refresh: UserRefresh }
type PurchaseFulfillment =
    | { status: 'order-complete'; purchaseStatus: Awaited<ReturnType<typeof processInGamePaymentPurchase>> }
    | { status: 'order-failed'; error: unknown; paymentReceived: boolean; refresh: UserRefresh | undefined }

export async function fulfillInGamePayment(params: FulfillmentParams<Topup>): Promise<TopupFulfillment>
export async function fulfillInGamePayment(
    params: FulfillmentParams<PurchasePaymentOrder>,
): Promise<PurchaseFulfillment>
export async function fulfillInGamePayment({
    api,
    clid,
    order,
    payment,
    refreshUser,
}: FulfillmentParams<PaymentOrder>): Promise<TopupFulfillment | PurchaseFulfillment> {
    if (order.type === 'topup') {
        return { status: 'topup-complete' as const, refresh: await refreshUserSafely(refreshUser) }
    }

    const transactionId =
        order.type === 'order' && payment?.amount === order.gamAmount ? payment.transactionId : undefined

    try {
        const purchaseStatus = await processInGamePaymentPurchase({ api, clid, order, transactionId })
        return { status: 'order-complete' as const, purchaseStatus }
    } catch (error) {
        const paymentReceived = Boolean(payment)
        return {
            status: 'order-failed' as const,
            error,
            paymentReceived,
            refresh: paymentReceived ? await refreshUserSafely(refreshUser) : undefined,
        }
    }
}

async function refreshUserSafely(refreshUser: () => Promise<User | null>) {
    try {
        return { user: await refreshUser(), error: undefined }
    } catch (error) {
        return { user: null, error }
    }
}

function processPurchaseOrder(
    api: ApiRequests,
    data: PurchasePaymentOrder,
    { clid, transactionId, url }: { clid?: string; transactionId?: string; url?: string },
) {
    let body: string

    if (data.type === 'order') {
        // gamAmount is the wallet-side view only; the backend converts `amount`/`currency` itself
        const { gameHru, attributes, gamAmount, ...orderObject } = data
        const attributesWithContext = isPortalContextClid(clid) ? { ...attributes, context: 'portal' } : attributes
        body = JSON.stringify({
            order: orderObject,
            gameHru,
            attributes: attributesWithContext,
            ...(transactionId ? { transactionId } : {}),
        })
    } else {
        const { amount, durationInDays, externalId, attributes, subscription } = data
        body = JSON.stringify({ order: { amount, durationInDays, externalId, attributes, subscription } })
    }

    if (url) {
        return api.request<PurchaseStatus>(url, { method: 'POST', body })
    }

    return api.signedRequest<PurchaseStatus>(CURRENCY_PURCHASE_URL, { method: 'POST', body })
}

export function isPortalContextClid(clid?: string) {
    return Boolean(clid && Object.values(PLAYGAMA_CLIDS).includes(clid))
}
